import { useState, type ReactElement } from 'react';
import { useSession } from '../../auth';
import { trackFailed } from '../../../../../shared-web/track';
import './PendingDeletionScreen.css';

/**
 * What a signed-in, scheduled-for-deletion account sees instead of the product.
 *
 * Signing in no longer cancels a pending deletion (2026-08-21): the session
 * proves who you are, and this screen is where the account itself is decided.
 * Rendered by EmberApp in place of the router for every path, the same
 * all-or-nothing shape as the legal re-consent door, because there is no
 * signed-in surface that makes sense while the account is on its way out — the
 * server refuses every write from the same signal (`account_pending_deletion`).
 *
 * It names the day the data goes, from the server's own `purge_at` so this
 * screen, the delete modal, and the texted notice cannot quote different days.
 */
export function PendingDeletionScreen(): ReactElement {
  const { session, restoreAccount, logout } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const purgeAt = session?.deletion_purge_at || '';
  const parsed = purgeAt ? new Date(purgeAt) : null;
  const day = parsed && !Number.isNaN(parsed.getTime())
    ? parsed.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' })
    : '';

  const restore = async (): Promise<void> => {
    if (busy) return;
    setBusy(true);
    setError('');
    try {
      await restoreAccount();
      // The session refresh inside restoreAccount clears deletion_pending, so
      // this screen unmounts on its own. A full load is still the honest next
      // step: the server-rendered bootstrap for this page was built for a
      // deleted account.
      window.location.href = '/';
    } catch {
      setError("Couldn't restore your account right now. Try again.");
      trackFailed('account_restore', 'error');
      setBusy(false);
    }
  };

  const signOut = async (): Promise<void> => {
    try { await logout(); } catch { /* server revoke is best-effort */ }
    window.location.href = '/';
  };

  return (
    <main className="ember-pending-deletion" role="main" data-log-seen="pending_deletion_wall">
      <div className="ember-pending-deletion-card">
        <h1>Your account is scheduled for deletion</h1>
        <p>
          {day
            ? <>Everything you&apos;ve shared with Ember is set to be permanently deleted on {day}.</>
            : <>Everything you&apos;ve shared with Ember is set to be permanently deleted at the end of the 30 days.</>}
          {' '}Until then Ember stays paused — nothing new gets written down.
        </p>
        <p>Nothing has been deleted yet. If you want it back, restore it.</p>
        {error && <p className="ember-pending-deletion-error" role="alert">{error}</p>}
        <div className="ember-pending-deletion-actions">
          <button
            type="button"
            className="ember-btn ember-btn-primary"
            disabled={busy}
            data-log="account_restore_confirm"
            onClick={() => { void restore(); }}
          >
            {busy ? 'Restoring…' : 'Restore account'}
          </button>
          <button
            type="button"
            className="ember-btn ember-btn-ghost"
            disabled={busy}
            data-log="pending_deletion_logout"
            onClick={() => { void signOut(); }}
          >
            Log out
          </button>
        </div>
        <p className="ember-pending-deletion-note">
          You can also reply RESTORE to Ember from the phone you signed up with.
        </p>
      </div>
    </main>
  );
}
